import { useQuery } from '@apollo/client';
import { Bolt, Cancel, ContentCopy, MoreHoriz, PlayArrow } from '@mui/icons-material';
import { Box, Button, Divider, Menu as MuiMenu } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useUpdateDeviceSignalTags } from '~/api/device-signal-tags';
import { useDeleteDeviceSignal } from '~/api/device-signals';
import { useCancelDeviceSignal } from '~/api/device-signals/cancel';
import { useDuplicateDeviceSignal } from '~/api/device-signals/duplicate';
import { useRunDeviceSignal } from '~/api/device-signals/run';
import { useUpdateDeviceSignal } from '~/api/device-signals/update';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { ScrollableRouterTabs } from '~/components/RouterTabs';
import { Tooltip } from '~/components/Tooltip';
import { OutlinedIconButton, SaveButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { TagsAutocomplete } from '~/components/inputs/TagsAutocomplete/TagsAutocomplete';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import {
  SettingDescription,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { MoreAction, MoreDeleteAction } from '~/components/table';
import { Timestamps } from '~/components/timestamps';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { DeviceSignalKind } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useMoreActions } from '~/hooks/table';
import { DeviceSignalForm, ScheduleFields, serializeSchedule, useSchedule } from '../components';
import {
  cancelPrompt,
  runPrompt,
  signalPatchSchema,
  toActionJson,
  toEventJson,
  toTargetJson,
  type Action,
  type SignalPatch,
} from '../lib';
import { NetworkDeviceSignalSettingsDocument } from '../queries/settings.generated';
import { useTabs } from './lib/tabs';

export const DeviceSignalSettings = () => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  const [confirmCancel, confirmCancelProps] = useConfirmDialog();
  const [confirmRun, confirmRunProps] = useConfirmDialog();

  const navigate = useNavigate();

  const link = useLink();

  const tabs = useTabs();

  const params = useParams<{ deviceSignalId: string }>();

  const deviceSignalId = Number(params.deviceSignalId);

  const { data, loading, error } = useQuery(NetworkDeviceSignalSettingsDocument, {
    variables: { networkId: currentNetwork.id, deviceSignalId },
  });

  const [cancelDeviceSignal] = useCancelDeviceSignal();
  const [deleteDeviceSignal] = useDeleteDeviceSignal();
  const [duplicateDeviceSignal] = useDuplicateDeviceSignal();
  const [runDeviceSignal] = useRunDeviceSignal();
  const [updateDeviceSignal] = useUpdateDeviceSignal();
  const [updateTags] = useUpdateDeviceSignalTags();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const signal = data?.network?.deviceSignal;

  const [moreMenuProps, moreActionProps, moreTableActionProps] =
    useMoreActions<NonNullable<typeof signal>>();

  const scheduleFormik = useSchedule(signal?.schedule);
  const signalTags = useMemo(
    () => data?.network?.deviceSignalTags.map((t) => t.name) ?? [],
    [data?.network?.deviceSignalTags],
  );

  const handleTagChange = useCallback(
    async (_event: React.SyntheticEvent, newValue: string[]) => {
      await updateTags({ variables: { taggableId: deviceSignalId, tags: newValue } });
    },
    [deviceSignalId, updateTags],
  );

  const initialValues = useMemo(() => {
    return {
      action: toActionJson(signal?.action) as Action | null, // initially may be invalid
      enabled: signal?.enabled ?? true,
      event: toEventJson(signal?.event),
      eventTypeId: signal?.eventType?.id ?? null,
      kind: signal?.kind ?? DeviceSignalKind.Manual,
      name: signal?.name ?? '',
      target: toTargetJson(signal),
    };
  }, [signal]);

  const formik = useFormik<SignalPatch>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      await updateDeviceSignal({
        variables: {
          id: deviceSignalId,
          patch: {
            ...signalPatchSchema.cast(values),
            // Event signals run at their event's time, so they never carry a schedule.
            schedule:
              values.kind === DeviceSignalKind.Event
                ? null
                : serializeSchedule(scheduleFormik.values),
          },
        },
      });
      formik.resetForm({ values });
    },
    validateOnMount: true,
    validationSchema: signalPatchSchema,
  });

  const isEvent = formik.values.kind === DeviceSignalKind.Event;

  const handleCancelClick = useCallback(async () => {
    if (!(await confirmCancel())) return;
    await cancelDeviceSignal({
      variables: { input: { id: deviceSignalId } },
    });
  }, [cancelDeviceSignal, confirmCancel, deviceSignalId]);

  const handleDelete = useCallback(async () => {
    if (!(await confirmDelete())) return;
    await deleteDeviceSignal({
      variables: { input: { signalIds: [deviceSignalId] } },
    });
    navigate(link('/device-signals'), { replace: true });
  }, [confirmDelete, deleteDeviceSignal, deviceSignalId, link, navigate]);

  const handleDuplicate = useCallback(async () => {
    const result = await duplicateDeviceSignal({
      variables: { input: { deviceSignalId } },
    });
    const duplicateId = result.data?.duplicateDeviceSignal?.deviceSignal.id;
    if (!duplicateId) return;
    navigate(link(`/device-signals/${duplicateId}`));
  }, [deviceSignalId, duplicateDeviceSignal, link, navigate]);

  const handleRunClick = useCallback(async () => {
    if (!(await confirmRun())) return;
    await runDeviceSignal({
      variables: { input: { id: deviceSignalId } },
    });
  }, [confirmRun, deviceSignalId, runDeviceSignal]);

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <Toolbar
          actions={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SaveButton
                disabled={
                  formik.isSubmitting ||
                  !formik.isValid ||
                  !(isEvent || scheduleFormik.isValid) ||
                  (!formik.dirty && !scheduleFormik.dirty)
                }
                type="submit"
                form="update-signal"
              />

              <Tooltip
                title={
                  data?.network?.deviceSignal.canRun.value
                    ? t('pages.device_signals.list.actions.run_tooltip')
                    : data?.network?.deviceSignal.canRun.reasons?.fullMessages.join(', ') ?? ''
                }
              >
                <span style={{ display: 'flex' }}>
                  <Button
                    disabled={!data?.network?.deviceSignal.canRun.value}
                    color="primary"
                    startIcon={<PlayArrow />}
                    variant="contained"
                    onClick={handleRunClick}
                  >
                    {t('pages.device_signals.buttons.run')}
                  </Button>
                </span>
              </Tooltip>

              {data?.network?.deviceSignal.action.__typename === 'SignalActionPlayShow' && (
                <Button
                  disabled={!data.network.deviceSignal.canCancel.value}
                  color="error"
                  startIcon={<Cancel />}
                  variant="contained"
                  onClick={handleCancelClick}
                >
                  {t('common.buttons.cancel')}
                </Button>
              )}
              <OutlinedIconButton
                label={t('common.buttons.more_actions')}
                onClick={signal ? moreTableActionProps(signal).onClick : undefined}
                size="large"
              >
                <MoreHoriz />
              </OutlinedIconButton>
            </Box>
          }
          breadcrumbsLabel={<RouterBreadcrumbs />}
          titleIcon={<Bolt />}
          titleText={data?.network?.deviceSignal.name}
        />

        <ScrollableRouterTabs current={t('common.labels.details')} tabs={tabs} />

        <MuiMenu
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          {...moreMenuProps}
        >
          <MoreAction
            {...moreActionProps}
            Icon={ContentCopy}
            onClick={() => void handleDuplicate()}
            title={t('pages.device_signals.list.menu.duplicate')}
          />
          <Divider />
          <MoreDeleteAction
            {...moreActionProps}
            disabled={!signal?.canDestroy.value}
            tooltip={signal?.canDestroy.reasons?.fullMessages.join(', ') ?? ''}
            onClick={() => void handleDelete()}
          />
        </MuiMenu>

        <PageContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DeviceSignalForm formik={formik} id="update-signal" />
            <Box>
              <SettingsHead>{t('pages.device_signals.schedule_section.header')}</SettingsHead>
              <SettingsBody>
                <ScheduleFields disabled={isEvent} formik={scheduleFormik} />
              </SettingsBody>
            </Box>
            <Box>
              <SettingsHead>{t('common.labels.tags')}</SettingsHead>
              <SettingsBody>
                <SettingsData className="last">
                  <SettingLabelWrapper>
                    <SettingName>{t('common.labels.tags')}</SettingName>
                    <SettingDescription>
                      {t('pages.device_signals.settings.fields.tags.description')}
                    </SettingDescription>
                  </SettingLabelWrapper>
                  <SettingInputWrapper>
                    <TagsAutocomplete
                      options={signalTags}
                      value={Array.from(signal?.tags ?? [])}
                      onChange={handleTagChange}
                      placeholder={t(
                        'pages.device_signals.settings.fields.tags.select_placeholder',
                      )}
                    />
                  </SettingInputWrapper>
                </SettingsData>
              </SettingsBody>
            </Box>
            {currentUser.admin && (
              <Timestamps
                createdAt={data?.network?.deviceSignal.createdAt ?? ''}
                updatedAt={data?.network?.deviceSignal.updatedAt ?? ''}
              />
            )}
          </Box>
        </PageContainer>
        <ConfirmDialog
          {...confirmDeleteProps}
          confirm={t('common.dialogs.permanently_delete')}
          deleteConfirm
          prompt={
            <span>
              {t('pages.device_signals.settings.delete_dialog.prompt')}{' '}
              <b>{t('common.warnings.cannot_be_undone')}</b>
            </span>
          }
          title={t('pages.device_signals.settings.delete_dialog.title')}
        />
        {data?.network?.deviceSignal && (
          <>
            <ConfirmDialog
              {...confirmCancelProps}
              confirm={t('pages.device_signals.list.dialogs.cancel.confirm')}
              deleteConfirm={false}
              prompt={cancelPrompt(data.network.deviceSignal)}
              title={t('pages.device_signals.list.dialogs.cancel.title')}
            />
            <ConfirmDialog
              {...confirmRunProps}
              confirm={t('pages.device_signals.list.dialogs.run.confirm')}
              deleteConfirm={false}
              prompt={runPrompt(data.network.deviceSignal)}
              title={t('pages.device_signals.list.dialogs.run.title')}
            />
          </>
        )}
      </>
    </LoadingPane>
  );
};
